// src/components/production/PrazosControleSubTab.js
import React, { useState, useMemo } from "react";
import {
  Clock,
  AlertTriangle,
  CheckCircle2,
  Calendar,
  Layers,
  Search,
  Filter,
  ArrowRight,
  TrendingDown,
  AlertCircle,
  Package,
  ChevronRight,
  ExternalLink,
  RefreshCw,
  Sparkles,
  User,
  ShieldAlert,
  SlidersHorizontal,
} from "lucide-react";
import { useProductionContext } from "../../contexts/ProductionContext";
import { useCommunication } from "../../contexts/CommunicationContext";
import { getModelFromSku } from "../../utils/skuUtils";
import {
  analisarPrazo,
  formatDataComDiaSemana,
  formatDiasUteis,
  STATUS_PRIORITY,
  getMostCriticalStatus,
  ETAPAS_CANONICAS,
} from "../../utils/prazoEngine";
import { PRODUCTION_STATUS_CONFIG, KANBAN_ORDER } from "../../config/productionStatuses";
import PrazoDetalhesModal from "./PrazoDetalhesModal";

export default function PrazosControleSubTab({
  user,
  orders = [],
  findCatalogItem,
  onMoveStatus,
}) {
  const { prazosMap = {}, temposEtapaMap = {} } = useProductionContext() || {};
  const { openQuickOrder } = useCommunication() || {};

  // Estados de Filtros e Busca
  const [statusFilter, setStatusFilter] = useState("all"); // all | VAI_ATRASAR | ATRASADO | EM_REVISAO | APERTADO | NO_PRAZO | PRONTO
  const [searchText, setSearchText] = useState("");
  const [etapaFilter, setEtapaFilter] = useState("all");
  const [modeloFilter, setModeloFilter] = useState("all");
  const [viewMode, setViewMode] = useState("orders"); // "orders" (agrupado por pedido) | "items" (peças avulsas)
  const [sortBy, setSortBy] = useState("urgencia"); // "urgencia" | "folga" | "decorrido" | "numero"

  // Estado para abrir modal de detalhes
  const [selectedOrderForModal, setSelectedOrderForModal] = useState(null);

  // 1. Processamento e Análise Temporal de cada peça
  const analyzedItems = useMemo(() => {
    return orders.map((order) => {
      const sku = order.sku || order.order?.sku || "";
      const modelo = getModelFromSku(sku);
      const prazoRecord = modelo ? prazosMap[modelo] : null;
      const prazoPrometido = prazoRecord?.prazo_prometido;
      const fatorRisco = prazoRecord?.fator_risco || 0;

      const dataCompra =
        order.customCreatedAt ||
        order.createdAt ||
        order.order?.created_at ||
        order.date ||
        order.created;

      const statusAtual = order.status || order.production_step || "SOLICITACAO";
      const isIntercepted = Boolean(order.isInterceptedPE || order.isIntercepted);

      const analise = analisarPrazo({
        dataCompra,
        statusAtual,
        prazoPrometido,
        temposEtapa: temposEtapaMap,
        modelo,
        isIntercepted,
        fatorRisco,
      });

      const orderNumber =
        order.order?.number ||
        order.order_number ||
        order.orderNumber ||
        order.id ||
        "-";

      const customerName =
        order.customer_name ||
        order.customerName ||
        order.order?.customer_name ||
        order.full_data?.customer_name ||
        order.full_data?.order?.customer_name ||
        "";

      return {
        ...order,
        sku,
        modelo,
        orderNumber: String(orderNumber).replace(/[[\]#]/g, "").trim(),
        customerName,
        dataCompra,
        etapaProducao: statusAtual,
        statusAtual: statusAtual,
        status: statusAtual,
        prazoStatus: analise.status,
        analise,
      };
    });
  }, [orders, prazosMap, temposEtapaMap]);

  // 2. Agrupamento por Pedido (Order Groups)
  const orderGroups = useMemo(() => {
    const map = new Map();

    analyzedItems.forEach((item) => {
      const num = item.orderNumber || item.id;
      if (!map.has(num)) {
        map.set(num, {
          ...item,
          orderNumber: num,
          customerName: item.customerName,
          dataCompra: item.dataCompra,
          items: [],
        });
      }
      map.get(num).items.push(item);
    });

    return Array.from(map.values()).map((group) => {
      const statuses = group.items.map((it) => it.analise.status);
      const mostCriticalStatus = getMostCriticalStatus(statuses);
      const criticalItem =
        [...group.items].sort(
          (a, b) =>
            (STATUS_PRIORITY[a.analise.status] || 99) -
            (STATUS_PRIORITY[b.analise.status] || 99)
        )[0] || group.items[0];

      // Atraso máximo e folga mínima do grupo
      const maxAtraso = Math.max(...group.items.map((i) => i.analise.atrasoEsperado || 0));
      const minFolgaVal = Math.min(...group.items.map((i) => i.analise.folga ?? 999));
      const minFolga = minFolgaVal === 999 ? 0 : minFolgaVal;

      return {
        ...group,
        prazoStatus: mostCriticalStatus,
        criticalItem,
        maxAtraso,
        minFolga,
        analise: criticalItem.analise,
      };
    });
  }, [analyzedItems]);

  // 3. Contadores para KPIs no Topo
  const stats = useMemo(() => {
    const list = viewMode === "orders" ? orderGroups : analyzedItems;
    let total = list.length;
    let vaiAtrasar = 0;
    let atrasado = 0;
    let emRevisao = 0;
    let apertado = 0;
    let noPrazo = 0;
    let pronto = 0;

    list.forEach((item) => {
      const st = item.prazoStatus || item.analise?.status || item.status;
      if (st === "VAI_ATRASAR") vaiAtrasar++;
      else if (st === "ATRASADO") atrasado++;
      else if (st === "EM_REVISAO") emRevisao++;
      else if (st === "APERTADO") apertado++;
      else if (st === "NO_PRAZO") noPrazo++;
      else if (st === "PRONTO" || st === "ENVIADO") pronto++;
    });

    return {
      total,
      vaiAtrasar,
      atrasado,
      emRevisao,
      apertado,
      noPrazo,
      pronto,
    };
  }, [orderGroups, analyzedItems, viewMode]);

  // 4. Lista única de Modelos e Etapas para filtros
  const modelosDisponiveis = useMemo(() => {
    const set = new Set();
    analyzedItems.forEach((i) => {
      if (i.modelo) set.add(i.modelo);
    });
    return Array.from(set).sort();
  }, [analyzedItems]);

  // 5. Filtragem e Ordenação
  const filteredList = useMemo(() => {
    const source = viewMode === "orders" ? orderGroups : analyzedItems;

    let result = source.filter((entry) => {
      const itemStatus = entry.prazoStatus || entry.analise?.status || entry.status;

      // Filtro de Status
      if (statusFilter !== "all" && itemStatus !== statusFilter) {
        return false;
      }

      // Filtro de Busca Texto
      if (searchText.trim()) {
        const query = searchText.toLowerCase().trim();
        const matchesNum = String(entry.orderNumber || "").toLowerCase().includes(query);
        const matchesClient = String(entry.customerName || "").toLowerCase().includes(query);
        let matchesItem = false;

        if (viewMode === "orders") {
          matchesItem = entry.items.some(
            (it) =>
              String(it.sku || "").toLowerCase().includes(query) ||
              String(it.modelo || "").toLowerCase().includes(query)
          );
        } else {
          matchesItem =
            String(entry.sku || "").toLowerCase().includes(query) ||
            String(entry.modelo || "").toLowerCase().includes(query);
        }

        if (!matchesNum && !matchesClient && !matchesItem) return false;
      }

      // Filtro de Etapa
      if (etapaFilter !== "all") {
        if (viewMode === "orders") {
          const hasEtapa = entry.items.some(
            (it) =>
              (it.etapaProducao || it.statusAtual || it.analise?.etapaMapeada) ===
              etapaFilter
          );
          if (!hasEtapa) return false;
        } else {
          const st =
            entry.etapaProducao ||
            entry.statusAtual ||
            entry.analise?.etapaMapeada;
          if (st !== etapaFilter) return false;
        }
      }

      // Filtro de Modelo
      if (modeloFilter !== "all") {
        if (viewMode === "orders") {
          const hasModel = entry.items.some((it) => it.modelo === modeloFilter);
          if (!hasModel) return false;
        } else {
          if (entry.modelo !== modeloFilter) return false;
        }
      }

      return true;
    });

    // Ordenação
    result.sort((a, b) => {
      const statusA = a.prazoStatus || a.analise?.status || a.status;
      const statusB = b.prazoStatus || b.analise?.status || b.status;

      if (sortBy === "urgencia") {
        const prioA = STATUS_PRIORITY[statusA] || 99;
        const prioB = STATUS_PRIORITY[statusB] || 99;
        if (prioA !== prioB) return prioA - prioB;
        // Desempate por maior atraso esperado
        const atA = a.maxAtraso ?? a.analise?.atrasoEsperado ?? 0;
        const atB = b.maxAtraso ?? b.analise?.atrasoEsperado ?? 0;
        return atB - atA;
      }

      if (sortBy === "folga") {
        const folgaA = a.minFolga ?? a.analise?.folga ?? 999;
        const folgaB = b.minFolga ?? b.analise?.folga ?? 999;
        return folgaA - folgaB;
      }

      if (sortBy === "decorrido") {
        const decA = a.analise?.decorrido ?? 0;
        const decB = b.analise?.decorrido ?? 0;
        return decB - decA;
      }

      if (sortBy === "numero") {
        return String(b.orderNumber).localeCompare(String(a.orderNumber), undefined, {
          numeric: true,
        });
      }

      return 0;
    });

    return result;
  }, [
    viewMode,
    orderGroups,
    analyzedItems,
    statusFilter,
    searchText,
    etapaFilter,
    modeloFilter,
    sortBy,
  ]);

  // Helper visual de badges
  const getBadgeStyle = (st) => {
    switch (st) {
      case "VAI_ATRASAR":
        return {
          label: "VAI ATRASAR",
          badge: "bg-orange-500 text-white font-extrabold shadow-sm animate-pulse",
          cardBorder: "border-orange-300 bg-orange-50/30",
          accentColor: "text-orange-600",
        };
      case "ATRASADO":
        return {
          label: "ATRASADO",
          badge: "bg-red-600 text-white font-extrabold shadow-sm animate-pulse",
          cardBorder: "border-red-300 bg-red-50/40",
          accentColor: "text-red-600",
        };
      case "EM_REVISAO":
        return {
          label: "EM REVISÃO",
          badge: "bg-amber-500 text-white font-black",
          cardBorder: "border-amber-300 bg-amber-50/30",
          accentColor: "text-amber-600",
        };
      case "APERTADO":
        return {
          label: "APERTADO",
          badge: "bg-yellow-500 text-slate-900 font-extrabold",
          cardBorder: "border-yellow-300 bg-yellow-50/20",
          accentColor: "text-yellow-700",
        };
      case "NO_PRAZO":
        return {
          label: "NO PRAZO",
          badge: "bg-emerald-600 text-white font-bold",
          cardBorder: "border-emerald-200 bg-white",
          accentColor: "text-emerald-600",
        };
      case "PRONTO":
        return {
          label: "PRONTO",
          badge: "bg-emerald-700 text-white font-black",
          cardBorder: "border-emerald-300 bg-emerald-50/20",
          accentColor: "text-emerald-700",
        };
      default:
        return {
          label: "SEM PRAZO",
          badge: "bg-slate-400 text-white font-medium",
          cardBorder: "border-slate-200 bg-white",
          accentColor: "text-slate-500",
        };
    }
  };

  return (
    <div className="flex flex-col h-full w-full overflow-hidden bg-slate-50">
      {/* 1. TOPO: CARDS DE KPI & INDICADORES TEMPORAIS */}
      <div className="p-4 bg-white border-b border-slate-200 shrink-0 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-base font-black text-slate-800 flex items-center gap-2">
              <Clock className="text-blue-600" size={18} />
              Controle & Previsão de Prazos da Oficina
            </h2>
            <p className="text-xs text-slate-500">
              Visão focada na esteira de produção para intervenção preventiva contra atrasos.
            </p>
          </div>

          {/* Toggle de Agrupamento */}
          <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl border border-slate-200 text-xs">
            <button
              onClick={() => setViewMode("orders")}
              className={`px-3 py-1.5 rounded-lg font-bold transition-all cursor-pointer ${
                viewMode === "orders"
                  ? "bg-white text-blue-700 shadow-xs"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Agrupado por Pedido ({orderGroups.length})
            </button>
            <button
              onClick={() => setViewMode("items")}
              className={`px-3 py-1.5 rounded-lg font-bold transition-all cursor-pointer ${
                viewMode === "items"
                  ? "bg-white text-blue-700 shadow-xs"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Peças Individuais ({analyzedItems.length})
            </button>
          </div>
        </div>

        {/* CARDS DE KPI CLICÁVEIS */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-7 gap-2 pt-1">
          {/* TOTAL */}
          <div
            onClick={() => setStatusFilter("all")}
            className={`p-2.5 rounded-xl border cursor-pointer transition-all ${
              statusFilter === "all"
                ? "bg-slate-900 text-white border-slate-900 shadow-sm"
                : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
            }`}
          >
            <span className="text-[10px] font-bold uppercase opacity-80 block">
              Total Ativos
            </span>
            <span className="text-lg font-black">{stats.total}</span>
          </div>

          {/* VAI ATRASAR (DESTAQUE MÁXIMO) */}
          <div
            onClick={() => setStatusFilter("VAI_ATRASAR")}
            className={`p-2.5 rounded-xl border cursor-pointer transition-all ${
              statusFilter === "VAI_ATRASAR"
                ? "bg-orange-600 text-white border-orange-600 shadow-md ring-2 ring-orange-400"
                : "bg-orange-50/80 text-orange-900 border-orange-200 hover:border-orange-400"
            }`}
          >
            <span className="text-[10px] font-extrabold uppercase flex items-center gap-1">
              <AlertTriangle size={11} /> Vai Atrasar
            </span>
            <span className="text-lg font-black">{stats.vaiAtrasar}</span>
          </div>

          {/* ATRASADOS */}
          <div
            onClick={() => setStatusFilter("ATRASADO")}
            className={`p-2.5 rounded-xl border cursor-pointer transition-all ${
              statusFilter === "ATRASADO"
                ? "bg-red-600 text-white border-red-600 shadow-md ring-2 ring-red-400"
                : "bg-red-50/80 text-red-900 border-red-200 hover:border-red-400"
            }`}
          >
            <span className="text-[10px] font-extrabold uppercase flex items-center gap-1">
              <AlertCircle size={11} /> Atrasados
            </span>
            <span className="text-lg font-black">{stats.atrasado}</span>
          </div>

          {/* EM REVISÃO */}
          <div
            onClick={() => setStatusFilter("EM_REVISAO")}
            className={`p-2.5 rounded-xl border cursor-pointer transition-all ${
              statusFilter === "EM_REVISAO"
                ? "bg-amber-600 text-white border-amber-600 shadow-sm"
                : "bg-amber-50/80 text-amber-900 border-amber-200 hover:border-amber-400"
            }`}
          >
            <span className="text-[10px] font-extrabold uppercase flex items-center gap-1">
              <Clock size={11} /> Em Revisão
            </span>
            <span className="text-lg font-black">{stats.emRevisao}</span>
          </div>

          {/* APERTADO */}
          <div
            onClick={() => setStatusFilter("APERTADO")}
            className={`p-2.5 rounded-xl border cursor-pointer transition-all ${
              statusFilter === "APERTADO"
                ? "bg-yellow-500 text-slate-900 border-yellow-500 shadow-sm font-bold"
                : "bg-yellow-50/80 text-yellow-900 border-yellow-200 hover:border-yellow-400"
            }`}
          >
            <span className="text-[10px] font-extrabold uppercase flex items-center gap-1">
              <Clock size={11} /> Apertados
            </span>
            <span className="text-lg font-black">{stats.apertado}</span>
          </div>

          {/* NO PRAZO */}
          <div
            onClick={() => setStatusFilter("NO_PRAZO")}
            className={`p-2.5 rounded-xl border cursor-pointer transition-all ${
              statusFilter === "NO_PRAZO"
                ? "bg-emerald-600 text-white border-emerald-600 shadow-sm"
                : "bg-emerald-50/80 text-emerald-900 border-emerald-200 hover:border-emerald-400"
            }`}
          >
            <span className="text-[10px] font-bold uppercase flex items-center gap-1">
              <CheckCircle2 size={11} /> No Prazo
            </span>
            <span className="text-lg font-black">{stats.noPrazo}</span>
          </div>

          {/* PRONTOS */}
          <div
            onClick={() => setStatusFilter("PRONTO")}
            className={`p-2.5 rounded-xl border cursor-pointer transition-all ${
              statusFilter === "PRONTO"
                ? "bg-emerald-800 text-white border-emerald-800 shadow-sm"
                : "bg-emerald-100/60 text-emerald-950 border-emerald-300 hover:border-emerald-500"
            }`}
          >
            <span className="text-[10px] font-bold uppercase flex items-center gap-1">
              ✓ Prontos
            </span>
            <span className="text-lg font-black">{stats.pronto}</span>
          </div>
        </div>
      </div>

      {/* 2. BARRA DE FILTROS E BUSCA */}
      <div className="p-3 bg-white border-b border-slate-200 shrink-0 flex flex-wrap items-center justify-between gap-3 text-xs">
        {/* Campo de Busca */}
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-2.5 text-slate-400" size={15} />
          <input
            type="text"
            placeholder="Buscar por nº do pedido, SKU, cliente ou modelo..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="w-full pl-9 pr-3 py-1.5 rounded-lg border border-slate-200 focus:outline-none focus:border-blue-500 text-xs bg-slate-50 focus:bg-white"
          />
        </div>

        {/* Filtros Dropdown */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Etapa */}
          <select
            value={etapaFilter}
            onChange={(e) => setEtapaFilter(e.target.value)}
            className="px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-700 font-medium text-xs focus:outline-none focus:border-blue-500 cursor-pointer"
          >
            <option value="all">Todas as Etapas</option>
            {KANBAN_ORDER.map((etp) => (
              <option key={etp} value={etp}>
                {PRODUCTION_STATUS_CONFIG[etp]?.label || etp}
              </option>
            ))}
          </select>

          {/* Modelo */}
          <select
            value={modeloFilter}
            onChange={(e) => setModeloFilter(e.target.value)}
            className="px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-700 font-medium text-xs focus:outline-none focus:border-blue-500 cursor-pointer"
          >
            <option value="all">Todos os Modelos</option>
            {modelosDisponiveis.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>

          {/* Ordenação */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-700 font-bold text-xs focus:outline-none focus:border-blue-500 cursor-pointer"
          >
            <option value="urgencia">⚡ Mais Urgentes / Críticos</option>
            <option value="folga">Menor Folga de Prazo</option>
            <option value="decorrido">Mais Antigos na Oficina</option>
            <option value="numero">Nº do Pedido</option>
          </select>

          {/* Limpar Filtros */}
          {(statusFilter !== "all" ||
            searchText ||
            etapaFilter !== "all" ||
            modeloFilter !== "all") && (
            <button
              onClick={() => {
                setStatusFilter("all");
                setSearchText("");
                setEtapaFilter("all");
                setModeloFilter("all");
              }}
              className="px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold transition-colors cursor-pointer"
            >
              Limpar Filtros
            </button>
          )}
        </div>
      </div>

      {/* 3. LISTAGEM DE CARDS */}
      <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
        {filteredList.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400 gap-2">
            <Clock size={36} className="text-slate-300" />
            <span className="font-medium text-sm">
              Nenhum pedido ou peça encontrada com os filtros selecionados.
            </span>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {filteredList.map((entry) => {
              const badge = getBadgeStyle(entry.prazoStatus || entry.analise?.status || entry.status);
              const analise = entry.analise;
              const isGroup = viewMode === "orders";
              const itemsList = isGroup ? entry.items : [entry];

              return (
                <div
                  key={entry.orderNumber ? `${entry.orderNumber}_${entry.id}` : entry.id}
                  className={`border rounded-xl p-4 flex flex-col justify-between gap-3 shadow-xs hover:shadow-md transition-all ${badge.cardBorder}`}
                >
                  {/* CABEÇALHO DO CARD */}
                  <div className="space-y-1.5">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => openQuickOrder && openQuickOrder(entry.orderNumber)}
                            className="font-black text-sm text-slate-800 hover:text-blue-600 hover:underline cursor-pointer flex items-center gap-1"
                            title="Abrir Ficha Rápida do Pedido"
                          >
                            <span>Pedido #{entry.orderNumber}</span>
                            <ExternalLink size={12} className="opacity-60" />
                          </button>

                          {isGroup && itemsList.length > 1 && (
                            <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-[10px] font-bold">
                              {itemsList.length} itens
                            </span>
                          )}
                        </div>

                        {entry.customerName && (
                          <div className="text-[11px] text-slate-500 font-medium truncate max-w-[200px]">
                            {entry.customerName}
                          </div>
                        )}
                      </div>

                      {/* BADGE DE STATUS */}
                      <span
                        className={`text-[9px] uppercase px-2 py-0.5 rounded-full ${badge.badge}`}
                      >
                        {badge.label}
                      </span>
                    </div>

                    {/* INDICADOR DE CRONOGRAMA & DATAS */}
                    <div className="bg-white/80 border border-black/5 rounded-lg p-2.5 text-[11px] space-y-1.5">
                      <div className="flex items-center justify-between text-slate-600">
                        <span className="text-[10px] uppercase font-bold text-slate-400">
                          Data Prometida (Venda):
                        </span>
                        <span className="font-extrabold text-blue-700">
                          {analise.dataPrometidaFormatada}
                        </span>
                      </div>

                      <div className="flex items-center justify-between text-slate-600">
                        <span className="text-[10px] uppercase font-bold text-slate-400">
                          Previsão Oficina:
                        </span>
                        <span
                          className={`font-black ${
                            analise.status === "PRONTO"
                              ? "text-emerald-700"
                              : analise.status === "ATRASADO" || analise.status === "VAI_ATRASAR"
                              ? "text-red-600 font-extrabold"
                              : "text-emerald-700"
                          }`}
                        >
                          {analise.status === "PRONTO"
                            ? "Pronto na Oficina"
                            : analise.status === "EM_REVISAO"
                            ? "Aguardando Revisão"
                            : analise.dataPrevistaFormatada}
                        </span>
                      </div>

                      <div className="pt-1 border-t border-slate-100 flex items-center justify-between text-[10px]">
                        <span className="text-slate-500">
                          {formatDiasUteis(analise.decorrido)} d.ú. decorridos
                        </span>
                        <span className="font-mono font-bold">
                          {analise.status === "PRONTO" ? (
                            <span className="text-emerald-700">✓ Concluído</span>
                          ) : analise.status === "EM_REVISAO" ? (
                            <span className="text-amber-700">Revisão pendente</span>
                          ) : analise.atrasoEsperado > 0 ? (
                            <span className="text-red-600 font-black">
                              Atraso: +{formatDiasUteis(analise.atrasoEsperado)} d.ú.
                            </span>
                          ) : (
                            <span className="text-emerald-700 font-bold">
                              Folga: {formatDiasUteis(analise.folga)} d.ú.
                            </span>
                          )}
                        </span>
                      </div>
                    </div>

                    {/* DIAGNÓSTICO CURTO */}
                    <p className="text-[11px] text-slate-600 leading-tight">
                      {analise.descricaoRisco}
                    </p>
                  </div>

                  {/* LISTA DE PEÇAS / ETAPAS NO CARD */}
                  <div className="space-y-1.5 pt-1 border-t border-black/5">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                      Peça(s) & Etapa Atual:
                    </span>

                    <div className="space-y-1.5 max-h-36 overflow-y-auto custom-scrollbar">
                      {itemsList.map((it, idx) => {
                        const itAnalise = it.analise;
                        const itEtapa =
                          it.etapaProducao ||
                          it.statusAtual ||
                          it.order?.status ||
                          itAnalise.etapaMapeada;

                        return (
                          <div
                            key={it.id || idx}
                            className="bg-white p-2 rounded-lg border border-slate-200/80 flex items-center justify-between gap-2 text-xs"
                          >
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5 truncate">
                                <span className="font-mono font-bold text-slate-800 text-[11px] truncate">
                                  {it.sku || "Sem SKU"}
                                </span>
                                {it.modelo && (
                                  <span className="bg-slate-100 text-slate-600 text-[9px] px-1.5 py-0.2 rounded font-bold">
                                    {it.modelo}
                                  </span>
                                )}
                              </div>
                              <span className="text-[10px] text-slate-400">
                                {itAnalise.status === "PRONTO"
                                  ? "✓ Oficina Finalizada"
                                  : `Faltam ~${formatDiasUteis(itAnalise.faltam)} d.ú.`}
                              </span>
                            </div>

                            {/* SELETOR RÁPIDO DE ETAPA PARA INTERVENÇÃO PONTUAL */}
                            <div className="shrink-0">
                              <select
                                value={itEtapa}
                                onChange={(e) => {
                                  const newStatus = e.target.value;
                                  if (onMoveStatus && it.id) {
                                    onMoveStatus(it.id, newStatus);
                                  }
                                }}
                                className="text-[10px] font-bold py-1 px-1.5 rounded border border-slate-200 bg-slate-50 hover:bg-white text-slate-700 cursor-pointer focus:outline-none focus:border-blue-500"
                                title="Avançar ou alterar etapa da peça para evitar atraso"
                              >
                                {KANBAN_ORDER.map((etp) => (
                                  <option key={etp} value={etp}>
                                    {PRODUCTION_STATUS_CONFIG[etp]?.label || etp}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* RODAPÉ DO CARD COM AÇÕES */}
                  <div className="flex items-center justify-between pt-2 border-t border-black/5 text-xs">
                    <button
                      type="button"
                      onClick={() =>
                        setSelectedOrderForModal(
                          isGroup ? entry : { ...entry, items: [entry] }
                        )
                      }
                      className="text-blue-600 hover:text-blue-800 font-bold flex items-center gap-1 hover:underline cursor-pointer"
                    >
                      <span>Ver Esteira & Detalhes</span>
                      <ChevronRight size={13} />
                    </button>

                    <button
                      type="button"
                      onClick={() => openQuickOrder && openQuickOrder(entry.orderNumber)}
                      className="px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold transition-colors cursor-pointer text-[11px]"
                    >
                      Ficha do Pedido
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* MODAL DETALHADO DE ANÁLISE TEMPORAL (QUANDO CLICADO) */}
      {selectedOrderForModal && (
        <PrazoDetalhesModal
          order={selectedOrderForModal}
          isOpen={!!selectedOrderForModal}
          onClose={() => setSelectedOrderForModal(null)}
        />
      )}
    </div>
  );
}
